import React from 'react';
import axios from 'axios';
import errorReporter from '@app/errorReporter';
import { AuthContext } from 'contexts';
import { isAssumeOrgType } from 'utils';
import { Icon as NewIcon } from '@brokerbay/elements-web';
import Badge from '../Badge';
import { Text } from '../Typography';
import Icon from '../Icon';
import AppHeaderOverlay from './AppHeaderOverlay';
import { StyledHeaderItem } from './StyledComponents';
import {
  ConversationType,
  MessagingMe,
  generateConvoItem,
  cleanConvo,
} from './MessageComponents';

export { ConversationType, MessagingMe, generateConvoItem, cleanConvo };

type MessagesProps = {
  iconSizes: {
    width: number;
    height: number;
  };
  isMobile: boolean;
  closeMobileMenu: () => void;
  $height: number;
  showingsFeatureOn?: boolean;
};

const Messages: React.FC<MessagesProps> = ({
  iconSizes,
  isMobile,
  closeMobileMenu,
  $height,
  showingsFeatureOn,
}) => {
  const { auth, setPusherEvents } = React.useContext(AuthContext);
  const [me, setMe] = React.useState<MessagingMe>(null);
  const [convos, setConvos] = React.useState<ConversationType[]>([]);
  const [finishedSetup, setFinishedSetup] = React.useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [overlayVisible, setOverlayVisible] = React.useState(false);

  const getMe = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get<MessagingMe>(`/messaging/me`);
      setMe(data);
    } catch (err) {
      errorReporter.error(err);
    }
  };

  const socketConvoUpdated = (data: ConversationType) => {
    if (
      isAssumeOrgType(auth.org.type) ||
      !auth.isAssumedNotBB ||
      data.assumedBrokerageId.toString() === auth.org.id
    ) {
      setMe(curMe => {
        if (data.latestSender.toString() === curMe.id) return curMe;
        return {
          ...curMe,
          unreadConvos: [data].concat(
            curMe.unreadConvos.filter(({ id }) => id !== data.id),
          ),
        };
      });
      setConvos(curConvos =>
        [data].concat(curConvos.filter(({ id }) => id !== data.id)),
      );
    }
  };

  const socketReadAll = () => {
    setMe(curMe => ({ ...curMe, unreadConvos: [] }));
  };

  const socketRead = (convoRead: ConversationType) => {
    setMe(curMe => ({
      ...curMe,
      unreadConvos: curMe.unreadConvos.filter(
        curConvo => curConvo.id !== convoRead?.id,
      ),
    }));
    if (convoRead?.remove) {
      setConvos(curConvos =>
        curConvos.filter(convo => convo.id !== convoRead.id),
      );
    }
  };

  React.useEffect(() => {
    if (auth && !me) {
      getMe();
    }
  }, [auth, me]);

  React.useEffect(() => {
    const toggleSockets = (isBind: boolean) => {
      if (!setPusherEvents) return;
      setPusherEvents('userim', 'convoUpdated', socketConvoUpdated, isBind);
      setPusherEvents('userim', 'convoReadAll', socketReadAll, isBind);
      setPusherEvents('userim', 'convoRead', socketRead, isBind);
    };
    if (!isLoading && setPusherEvents) {
      toggleSockets(true);
      return () => toggleSockets(false);
    }
  }, [isLoading, setPusherEvents]);

  React.useEffect(() => {
    const getConvos = async () => {
      try {
        const { data } = await axios.get<ConversationType[]>(
          `/messaging/v1/convo`,
          { params: { page: '0' } },
        );

        setConvos(data);
      } catch (err) {
        errorReporter.error(err);
      }
      setIsLoading(false);
    };

    if (me && !finishedSetup) {
      getConvos();
      setFinishedSetup(true);
    }
  }, [me, finishedSetup]);

  const countUnread = me?.unreadConvos?.length || 0;

  const markAllRead = async () => {
    const backup = [...convos];
    const fakeResponse = convos.map(convo => ({ ...convo, isUnread: false }));
    setConvos(fakeResponse);
    try {
      await axios.post('/messaging/convo/read_all');
      setShowUnreadOnly(false);
    } catch (err) {
      errorReporter.error(err);
      setConvos(backup);
    }
  };

  const filteredConvos = React.useMemo(() => convos
      .map(convo => cleanConvo(convo, me))
      .filter(({ isUnread }) => !showUnreadOnly || isUnread), [me, convos, showUnreadOnly]);

  return (
    <AppHeaderOverlay
      OverlayButton={
        isMobile ? (
          <StyledHeaderItem
            $height={$height}
            $isMobile={isMobile}
            $showBackgroundColor
            onClick={() => setOverlayVisible(visible => !visible)}
          >
            {showingsFeatureOn ? (
              <NewIcon icon="messageFilled" color="default" size={24} />
            ) : (
              <Icon type="chatBubble" {...iconSizes} />
            )}
            <Text type="secondary">Chat</Text>
            <div>
              <Badge color="danger" count={countUnread} overflowCount={99} />
            </div>
          </StyledHeaderItem>
        ) : (
          <Badge
            // Do not show unread count when mls staff user is assuming another user
            count={auth?.assumerIdMlsStaff ? 0 : countUnread}
            overflowCount={99}
            className="app-header-button"
            color="danger"
          >
            <button className="app-header-button">
              {showingsFeatureOn ? (
                <NewIcon
                  icon="messageFilled"
                  color="white"
                  size={24}
                  style={{ verticalAlign: '-0.3em' }}
                />
              ) : (
                <Icon type="chatBubble" {...iconSizes} />
              )}
            </button>
          </Badge>
        )
      }
      overlayVisible={overlayVisible}
      closeMobileMenu={closeMobileMenu}
      setOverlayVisible={setOverlayVisible}
      title="Messages"
      emptyDescription="You have no Messages."
      pageUrl="/inbox"
      isLoading={isLoading}
      options={[
        {
          icon: showUnreadOnly ? 'chatBubbleSquare' : 'chatBubbleSquareDot',
          label: showUnreadOnly ? 'Show all' : 'Show unread only',
          onClick: () => setShowUnreadOnly(!showUnreadOnly),
        },
        {
          icon: 'check',
          label: 'Mark all as read',
          onClick: markAllRead,
        },
      ]}
      items={filteredConvos.map(convo => generateConvoItem({ convo, me }))}
    />
  );
};

export default Messages;
